import React, { useEffect, useState, useRef } from 'react';
import { CheckCircle, Printer, ArrowLeft, ShieldCheck, User, GraduationCap, Building } from 'lucide-react';
import { getOrCreateApplicationNo } from '../utils/applicationCounter';
import { sendPaymentSuccessEmail } from '../utils/emailService';

export default function SuccessPage({ onHomeClick }) {
  const [details, setDetails] = useState(null);
  const emailSentRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const txnId = params.get('txnid') || params.get('mihpayid') || `PAYU_${Date.now()}`;
    const amount = params.get('amount') || '500';
    const firstname = params.get('firstname') || params.get('name') || 'Applicant';
    const email = params.get('email') || 'applicant@formsadda.com';
    const phone = params.get('phone') || params.get('mobile') || '';
    const productinfo = params.get('productinfo') || 'Manipal University Jaipur';

    // Generate or retrieve sequential unique application number (e.g. Manipal-University-27001)
    const applicationNo = getOrCreateApplicationNo(txnId);
    const currentDate = new Date().toLocaleString('en-IN', {
      dateStyle: 'full',
      timeStyle: 'medium'
    });

    const paymentInfo = {
      applicationNo,
      txnId,
      amount,
      name: firstname,
      email,
      phone,
      course: productinfo.replace('Application Fee - ', ''),
      university: productinfo.replace('Application Fee - ', ''),
      date: currentDate,
      status: 'SUCCESS'
    };

    setDetails(paymentInfo);

    // Send payment invoice email to User & Admin (admin@formsadda.com) once
    if (!emailSentRef.current) {
      emailSentRef.current = true;
      sendPaymentSuccessEmail(paymentInfo);
    }
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const handleGoHome = () => {
    if (onHomeClick) {
      onHomeClick();
    } else {
      window.location.href = '/';
    }
  };

  if (!details) {
    return (
      <div style={{ padding: '4rem', textAlign: 'center' }}>
        <h2>Loading Payment Receipt...</h2>
      </div>
    );
  }

  return (
    <div className="payment-page-container" style={{ minHeight: '100vh', backgroundColor: '#F1F5F9', padding: '2rem 1rem' }}>
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #printable-invoice, #printable-invoice * {
            visibility: visible;
          }
          #printable-invoice {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            box-shadow: none !important;
            border: 1px solid #cbd5e1 !important;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Navigation / Header controls */}
        <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <button
            onClick={handleGoHome}
            className="btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#ffffff' }}
          >
            <ArrowLeft size={18} /> Back to Homepage
          </button>
          
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={handlePrint}
              className="btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#059669' }}
            >
              <Printer size={18} /> Download / Print Invoice
            </button>
          </div>
        </div>

        {/* Top Success Banner */}
        <div className="no-print" style={{
          backgroundColor: '#065F46',
          color: '#FFFFFF',
          borderRadius: '16px',
          padding: '1.75rem',
          textAlign: 'center',
          marginBottom: '1.5rem',
          boxShadow: '0 10px 25px rgba(6, 95, 70, 0.25)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#047857',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem auto',
            border: '3px solid #34D399'
          }}>
            <CheckCircle size={38} color="#34D399" />
          </div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 800, marginBottom: '0.35rem' }}>Payment Successful!</h1>
          <p style={{ fontSize: '1rem', color: '#A7F3D0', marginBottom: '1rem' }}>
            Your application registration has been confirmed via PayU Gateway.
          </p>
          <div style={{
            display: 'inline-block',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(4px)',
            padding: '0.5rem 1.25rem',
            borderRadius: '50px',
            fontSize: '0.95rem',
            fontWeight: 700,
            border: '1px solid rgba(255,255,255,0.3)'
          }}>
            Application No: <span style={{ color: '#FDE047', fontFamily: 'monospace', letterSpacing: '0.5px' }}>{details.applicationNo}</span>
          </div>
        </div>

        {/* Official Printable Invoice Document */}
        <div id="printable-invoice" style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '20px',
          padding: '2.5rem',
          boxShadow: '0 15px 35px rgba(15, 23, 42, 0.08)',
          border: '1px solid #E2E8F0',
          position: 'relative'
        }}>
          {/* Invoice Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #E2E8F0', paddingBottom: '1.5rem', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                <Building size={24} color="#FF6B00" />
                <span style={{ fontSize: '1.4rem', fontWeight: 900, color: '#0F2537' }}>ONLINE MANIPAL</span>
              </div>
              <p style={{ fontSize: '0.82rem', color: '#64748B', margin: 0 }}>
                Authorized Online MBA Admission Partner & Portal (FormsADDA)
              </p>
            </div>

            <div style={{ textAlign: 'right' }}>
              <div style={{
                backgroundColor: '#D1FAE5',
                color: '#065F46',
                padding: '0.35rem 0.85rem',
                borderRadius: '8px',
                fontWeight: 800,
                fontSize: '0.85rem',
                display: 'inline-block',
                marginBottom: '0.4rem',
                border: '1px solid #A7F3D0'
              }}>
                FEE RECEIPT - PAID
              </div>
              <p style={{ fontSize: '0.8rem', color: '#64748B', margin: 0 }}>
                Date: {details.date}
              </p>
            </div>
          </div>

          {/* Application No Highlight Banner inside Invoice */}
          <div style={{
            backgroundColor: '#F8FAFC',
            borderLeft: '5px solid #FF6B00',
            padding: '1rem 1.25rem',
            borderRadius: '0 12px 12px 0',
            marginBottom: '1.75rem',
            display: 'flex',
            justify: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}>
            <div>
              <span style={{ fontSize: '0.8rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>Unique Application Number</span>
              <div style={{ fontSize: '1.35rem', fontWeight: 900, color: '#0F2537', fontFamily: 'monospace' }}>{details.applicationNo}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: '0.8rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>PayU Reference ID</span>
              <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#059669', fontFamily: 'monospace' }}>{details.txnId}</div>
            </div>
          </div>

          {/* Two-Column Details Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
            {/* Student Details */}
            <div style={{ backgroundColor: '#F8FAFC', borderRadius: '14px', padding: '1.25rem', border: '1px solid #E2E8F0' }}>
              <h4 style={{ fontSize: '0.92rem', color: '#334155', fontWeight: 800, marginBottom: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <User size={16} color="#FF6B00" /> Applicant Information
              </h4>
              <div style={{ fontSize: '0.88rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div>
                  <span style={{ color: '#64748B', display: 'block', fontSize: '0.78rem' }}>Full Name</span>
                  <strong style={{ color: '#0F2537' }}>{details.name}</strong>
                </div>
                <div>
                  <span style={{ color: '#64748B', display: 'block', fontSize: '0.78rem' }}>Mobile Number</span>
                  <strong style={{ color: '#0F2537' }}>+91 {details.phone}</strong>
                </div>
                <div>
                  <span style={{ color: '#64748B', display: 'block', fontSize: '0.78rem' }}>Email Address</span>
                  <strong style={{ color: '#0F2537', wordBreak: 'break-all' }}>{details.email}</strong>
                </div>
              </div>
            </div>

            {/* University & Payment Gateway Details */}
            <div style={{ backgroundColor: '#F8FAFC', borderRadius: '14px', padding: '1.25rem', border: '1px solid #E2E8F0' }}>
              <h4 style={{ fontSize: '0.92rem', color: '#334155', fontWeight: 800, marginBottom: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <GraduationCap size={16} color="#FF6B00" /> Program & Gateway Info
              </h4>
              <div style={{ fontSize: '0.88rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div>
                  <span style={{ color: '#64748B', display: 'block', fontSize: '0.78rem' }}>Selected Course</span>
                  <strong style={{ color: '#0F2537' }}>{details.course || details.university}</strong>
                </div>
                <div>
                  <span style={{ color: '#64748B', display: 'block', fontSize: '0.78rem' }}>Payment Gateway</span>
                  <strong style={{ color: '#0F2537' }}>PayU Money (Merchant Key: 3bqRWp)</strong>
                </div>
                <div>
                  <span style={{ color: '#64748B', display: 'block', fontSize: '0.78rem' }}>Transaction Status</span>
                  <strong style={{ color: '#059669' }}>SUCCESS (Verified)</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Itemized Table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '2rem', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#0F2537', color: '#FFFFFF' }}>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'left', borderRadius: '8px 0 0 8px' }}>Description</th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>Qty</th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'right', borderRadius: '0 8px 8px 0' }}>Amount (INR)</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                <td style={{ padding: '1rem' }}>
                  <strong style={{ color: '#0F2537', display: 'block' }}>Registration Token & Application Fee</strong>
                  <span style={{ fontSize: '0.8rem', color: '#64748B' }}>
                    Provisional Admission Token for {details.university} ({details.applicationNo})
                  </span>
                </td>
                <td style={{ padding: '1rem', textAlign: 'center', color: '#475569' }}>1</td>
                <td style={{ padding: '1rem', textAlign: 'right', fontWeight: 800, color: '#0F2537' }}>₹{details.amount}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="2" style={{ padding: '1rem', textAlign: 'right', fontWeight: 800, fontSize: '1rem', color: '#0F2537' }}>
                  Total Amount Paid:
                </td>
                <td style={{ padding: '1rem', textAlign: 'right', fontWeight: 900, fontSize: '1.3rem', color: '#059669' }}>
                  ₹{details.amount}
                </td>
              </tr>
            </tfoot>
          </table>

          {/* Footer Authorization Stamp & Sign */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderTop: '2px dashed #CBD5E1', paddingTop: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#059669', fontSize: '0.82rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                <ShieldCheck size={16} /> Digitally Verified & Computer Generated Receipt
              </div>
              <p style={{ fontSize: '0.78rem', color: '#64748B', margin: 0 }}>
                Copy sent to student email (<strong style={{ color: '#334155' }}>{details.email}</strong>) & Admin (<strong style={{ color: '#334155' }}>admin@formsadda.com</strong>).
              </p>
            </div>

            <div style={{ textAlign: 'center', minWidth: '160px' }}>
              <div style={{
                fontFamily: 'Dancing Script, cursive, Georgia, serif',
                fontSize: '1.3rem',
                fontWeight: 700,
                color: '#0F2537',
                borderBottom: '1px solid #94A3B8',
                paddingBottom: '0.25rem',
                marginBottom: '0.25rem'
              }}>
                FormsADDA Admission Cell
              </div>
              <span style={{ fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Authorized Signatory</span>
            </div>
          </div>
        </div>

        {/* Bottom Actions Banner */}
        <div className="no-print" style={{ textAlign: 'center', marginTop: '2rem', paddingBottom: '2rem' }}>
          <p style={{ fontSize: '0.9rem', color: '#64748B', marginBottom: '1rem' }}>
            An academic advisor will contact you within 24 hours to proceed with document verification.
          </p>
          <button onClick={handleGoHome} className="btn-primary" style={{ padding: '0.85rem 2rem', fontSize: '1rem' }}>
            Return to Online Manipal Homepage
          </button>
        </div>
      </div>
    </div>
  );
}
